/*
  Split option select
  Shows one combined product option (e.g. "Offset & Length" with values like "0mm, 330mm")
  as two separate dropdowns ("Offset" and "Length").

  The real combined <select> stays in the DOM (hidden) and keeps driving <variant-selects>:
  whenever a split dropdown changes we pick the matching combined value on the hidden
  select and dispatch its change event, so the theme resolves the real variant
  (id, SKU, price, inventory) exactly as before.

  Rendered by snippets/product-variant-picker.liquid.
*/
if (!customElements.get('split-option-select')) {
  class SplitOptionSelect extends HTMLElement {
    connectedCallback() {
      this.variantSelects = this.closest('variant-selects');
      this.target = document.getElementById(this.dataset.targetId);
      this.optionIndex = Number(this.dataset.optionIndex);
      this.selects = Array.from(this.querySelectorAll('select[data-split-part]'));
      if (!this.variantSelects || !this.target || this.selects.length !== 2) return;

      try {
        this.variants = JSON.parse(this.querySelector('script[data-split-variants]').textContent);
      } catch (e) {
        this.variants = [];
      }
      // While anything is in stock, only in-stock combinations are offered.
      this.hasAvailable = this.variants.some((variant) => variant.available);

      this.onPartChange = this.onPartChange.bind(this);
      this.selects.forEach((select) => select.addEventListener('change', this.onPartChange));
      this.updateAvailability();
    }

    disconnectedCallback() {
      if (!this.selects) return;
      this.selects.forEach((select) => select.removeEventListener('change', this.onPartChange));
    }

    static splitValue(value) {
      const index = value.indexOf(',');
      if (index === -1) return [value.trim(), ''];
      return [value.slice(0, index).trim(), value.slice(index + 1).trim()];
    }

    // Current value of every other option, read live from the picker (null = not rendered / any).
    getOtherSelections() {
      const selections = [];
      const size = Number(this.variantSelects.dataset.optionsSize) || 0;
      for (let i = 0; i < size; i++) {
        if (i === this.optionIndex) {
          selections.push(null);
          continue;
        }
        const select = this.variantSelects.querySelector(`select[data-option-position="${i}"]`);
        const radio = this.variantSelects.querySelector(`input[data-option-position="${i}"]:checked`);
        selections.push(select ? select.value : radio ? radio.value : null);
      }
      return selections;
    }

    // Variants matching the other options, with their combined value split into parts.
    getCandidates() {
      const others = this.getOtherSelections();
      return this.variants
        .filter((variant) => others.every((value, i) => value === null || variant.options[i] === value))
        .map((variant) => ({
          available: variant.available,
          parts: SplitOptionSelect.splitValue(variant.options[this.optionIndex] || ''),
        }));
    }

    onPartChange(event) {
      // Keep <variant-selects> from handling our own selects; it reacts to the hidden one instead.
      event.stopPropagation();

      const [firstSelect, secondSelect] = this.selects;
      const candidates = this.getCandidates();

      // Changing the first part can leave the second one on a combination that isn't offered:
      // fall back to the first in-stock (or at least existing) value for the new first part.
      if (event.target === firstSelect) {
        const matches = candidates.filter((c) => c.parts[0] === firstSelect.value);
        const offered = matches.some((c) => c.parts[1] === secondSelect.value && this.isOffered([c]));
        if (!offered && matches.length) {
          const fallback = matches.find((c) => c.available) || matches[0];
          secondSelect.value = fallback.parts[1];
        }
      }

      this.updateAvailability();
      this.syncTarget();
    }

    syncTarget() {
      const [first, second] = this.selects.map((select) => select.value);
      const option = Array.from(this.target.options).find((opt) => {
        const parts = SplitOptionSelect.splitValue(opt.value);
        return parts[0] === first && parts[1] === second;
      });
      if (!option || option.selected) return;

      Array.from(this.target.options).forEach((opt) => opt.removeAttribute('selected'));
      option.setAttribute('selected', 'selected');
      this.target.value = option.value;
      this.target.dispatchEvent(new Event('change', { bubbles: true }));
    }

    updateAvailability() {
      const [firstSelect, secondSelect] = this.selects;
      const candidates = this.getCandidates();

      this.setOptionStates(firstSelect, (value) => candidates.filter((c) => c.parts[0] === value));
      this.setOptionStates(secondSelect, (value) =>
        candidates.filter((c) => c.parts[0] === firstSelect.value && c.parts[1] === value)
      );
    }

    isOffered(matches) {
      return this.hasAvailable ? matches.some((c) => c.available) : matches.length > 0;
    }

    // Combinations that aren't offered are hidden (and disabled, for browsers that ignore hidden <option>s).
    // The current selection is never hidden so the select doesn't jump.
    setOptionStates(select, getMatches) {
      Array.from(select.options).forEach((opt) => {
        const offered = opt.value === select.value || this.isOffered(getMatches(opt.value));
        opt.hidden = !offered;
        opt.disabled = !offered;
      });
    }
  }

  customElements.define('split-option-select', SplitOptionSelect);
}
